import { Box } from '@mui/material';
import { GridCellParams, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { Association } from 'src/shared/models/association/association';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { getAssociations } from 'src/shared/services/associationService';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { AssociationSearchFilter } from '../models/associationSearchFilter';

type AssociationDataGridProps = {
  searchFilter: AssociationSearchFilter;
  searchFilterCallback: (searchFilter: AssociationSearchFilter) => void;
};

export const AssociationDataGrid = (props: AssociationDataGridProps) => {
  const { searchFilter, searchFilterCallback } = props;
  const { t } = useTranslation(['association']);
  const { scrollIntoView, scrollRef } = useScrollIntoView();
  const isMounted = useIsMounted();
  const navigate = useNavigate();

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'localAss', sort: 'asc' }]);
  const [rows, setRows] = useState<Association[]>([]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });

  const updateData = <K extends keyof DataGridModel>(k: K, v: DataGridModel[K]) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
    }
    try {
      const result = await getAssociations(undefined, searchFilter);
      if (isMounted()) {
        const associationsResult = extendObjectWithId(result.data.results, 'localAss_id');
        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          setRows(associationsResult);
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
      }
    }
  }, [isMounted, searchFilter]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const onRowClick = (params) => {
    navigate(`${RoutesEnum.OrganizationAssocations}/${params.row.localAss_id}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'nr',
      headerName: t('association:tableHeaders.nr'),
      sortable: true,
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.nr || ''}</>;
      },
    },
    {
      field: 'localAss',
      headerName: t('association:tableHeaders.name'),
      sortable: true,
      flex: 2.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.localAss || ''}</>;
      },
    },
  ];

  return (
    <Box
      sx={{
        '& .MuiDataGrid-cell': {
          maxHeight: 'fit-content !important',
          overflow: 'auto',
          lineHeight: '16px !important',
          paddingTop: '10px !important',
          paddingBottom: '10px !important',
          display: 'flex !important',
          alignItems: 'flex-start !important',
        },
        '& .MuiDataGrid-viewport, & .MuiDataGrid-row, & .MuiDataGrid-renderingZone': {
          maxHeight: 'fit-content !important',
        },
      }}
      ref={scrollRef}
    >
      <DataGrid<Association>
        autoHeight
        onRowClick={onRowClick}
        columns={columns}
        rows={rows}
        dataModel={data}
        disableSelectionOnClick
        sortModel={sortModel}
        checkboxSelection={false}
        onSelectionModelChange={() => ConsoleHelper.log()}
        selectionModel={[]}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
          }
        }}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </Box>
  );
};
